import { useCallback } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface CheckboxOption {
  id: string;
  label: string;
  checked?: boolean;
}

export interface CheckboxSection {
  title: string;
  options: CheckboxOption[];
  columns?: 1 | 2;
}

interface CheckboxGroupProps {
  sections: CheckboxSection[];
  onChange?: (
    selectedOptions: { sectionTitle: string; selectedIds: string[] }[],
  ) => void;
  showSeparators?: boolean;
  showClearButton?: boolean;
  clearButtonText?: string;
  className?: string;
  disabledOptionIds?: string[];
}

export function CheckboxGroup({
  sections,
  onChange,
  showSeparators = true,
  showClearButton = true,
  clearButtonText = "Limpar Filtros",
  className,
  disabledOptionIds,
}: CheckboxGroupProps) {
  const getSelectedOptions = useCallback(
    (updatedSections: CheckboxSection[]) => {
      return updatedSections.map((section) => ({
        sectionTitle: section.title,
        selectedIds: section.options
          .filter((option) => option.checked)
          .map((option) => option.id),
      }));
    },
    [],
  );

  const handleCheckboxChange = (
    sectionIndex: number,
    optionId: string,
    checked: boolean,
  ) => {
    const updatedSections = sections.map((section, index) =>
      index === sectionIndex
        ? {
            ...section,
            options: section.options.map((option) =>
              option.id === optionId ? { ...option, checked } : option,
            ),
          }
        : section,
    );

    if (onChange) {
      onChange(getSelectedOptions(updatedSections));
    }
  };

  const clearAllFilters = () => {
    const clearedSections = sections.map((section) => ({
      ...section,
      options: section.options.map((option) => ({ ...option, checked: false })),
    }));

    if (onChange) {
      onChange(getSelectedOptions(clearedSections));
    }
  };

  const getTotalSelected = () => {
    return sections.reduce(
      (total, section) =>
        total + section.options.filter((option) => option.checked).length,
      0,
    );
  };

  return (
    <div className={cn("space-y-6", className)}>
      {showClearButton && (
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">
            {getTotalSelected()} filtros selecionados
          </span>
          <Button
            onClick={clearAllFilters}
            variant="outline"
            size="sm"
            disabled={getTotalSelected() === 0}
          >
            {clearButtonText}
          </Button>
        </div>
      )}

      {sections.map((section, sectionIndex) => (
        <div key={section.title}>
          {showSeparators && sectionIndex > 0 && <Separator className="mb-6" />}

          <div className="mb-4">
            <h3 className="text-sm font-normal">{section.title}</h3>
          </div>

          <div
            className={cn(
              "grid gap-4",
              section.columns === 1
                ? "grid-cols-1"
                : "grid-cols-1 md:grid-cols-2",
            )}
          >
            {section.options.map((option) => {
              const isDisabled = disabledOptionIds?.includes(option.id);
              return (
                <div key={option.id} className="flex items-center space-x-3">
                  <Checkbox
                    id={`${section.title}-${option.id}`}
                    checked={option.checked || false}
                    disabled={isDisabled}
                    onCheckedChange={(checked) =>
                      handleCheckboxChange(
                        sectionIndex,
                        option.id,
                        checked as boolean,
                      )
                    }
                  />
                  <Label
                    htmlFor={`${section.title}-${option.id}`}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    {option.label}
                  </Label>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
